import { useLocation } from 'react-router-dom';
import { siteConfig } from '@/mocks/siteConfig';
import { categoryData } from '@/mocks/categories';
import { faqData } from '@/mocks/faq';

interface SchemaOrgProps {
  type: 'Organization' | 'WebSite' | 'WebPage' | 'BreadcrumbList' | 'Article' | 'FAQPage';
  extra?: Record<string, unknown>;
}

function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: siteConfig.email,
    telephone: siteConfig.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteConfig.address,
      addressLocality: '北京市',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/99net',
    ],
  };
}

function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteConfig.url}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

function getBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

function getFAQSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

function getArticleSchema(article: {
  title: string;
  description: string;
  datePublished: string;
  author: string;
  url: string;
  imageUrl: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: article.datePublished,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    url: article.url,
    image: article.imageUrl,
  };
}

function getCategoryPageSchema(categoryName: string, url: string) {
  const cat = categoryData.find((c) => c.name === categoryName);
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - ${siteConfig.name}`,
    description: cat?.description || `${categoryName}分类下的所有文章`,
    url,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };
}

export default function SchemaOrg({ type, extra }: SchemaOrgProps) {
  const location = useLocation();
  let schema: Record<string, unknown>;

  switch (type) {
    case 'Organization':
      schema = getOrganizationSchema();
      break;
    case 'WebSite':
      schema = getWebSiteSchema();
      break;
    case 'WebPage':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: extra?.name || siteConfig.name,
        description: extra?.description || siteConfig.description,
        url: `${siteConfig.url}${location.pathname}`,
        isPartOf: {
          '@type': 'WebSite',
          name: siteConfig.name,
          url: siteConfig.url,
        },
      };
      break;
    case 'BreadcrumbList':
      schema = getBreadcrumbSchema((extra?.items as { name: string; url: string }[]) || []);
      break;
    case 'Article':
      schema = getArticleSchema(extra as unknown as {
        title: string;
        description: string;
        datePublished: string;
        author: string;
        url: string;
        imageUrl: string;
      });
      break;
    case 'FAQPage':
      schema = getFAQSchema();
      break;
    default:
      schema = {};
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export { getBreadcrumbSchema, getCategoryPageSchema, getArticleSchema };